import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import styles from './legal-structures.module.css';

import axios from '../../utils/axios';
import { ResultTable, ResultTableLeft, ResultTableMiddle, ResultTableRight } from '../../components/ResultTable';
import { ArchDiagram } from './components/ArchDiagram';
import { MainStructuresDetail } from './components/MainStructuresDetail';
import { Tips } from './components/Tips';
import { SelectedDataMulti, SelectedDataParams } from '../../components/SelectionPopup/SelectionPopup';
import LoaderComponent from '../../components/TransitionComponent/LoaderContent';
import TransitionComponent from '../../components/TransitionComponent/TransitionComponents';
import { LinkedButton } from '../../components/Button';
import { Button } from '../../components';
import { ArchData, ArchNodeItem } from '../../utils/generate-arch';
import { SelectionPopup } from '../../components/SelectionPopup';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';
import { useMenuStore } from '../../store/useMenuStore';
import { type LegalStructuresLayoutContextType } from './LegalStructuresLayout';

const LegalStructures = () => {
    // Get context from LegalStructuresLayout
    const context = useOutletContext<LegalStructuresLayoutContextType>();

    const {
        selectedData,
        setSelectedData,
        showPopup,
        setShowPopup,
        pageReady,
        tableUrl,
        setOnEdit,
        setTableUrl,
        setCheckClicked,
        setToggleRequest,
        checkClicked,
        toggleRequest,
    } = context;

    const [_searchParams, setSearchParams] = useSearchParams();
    const [showDetails, setShowDetails] = useState(false);
    const [activeInvestment, setActiveInvestment] = useState<ArchNodeItem | null>(null);
    const [loading, setLoading] = useState(false);
    const [archDiagramData, setArchDiagramData] = useState<ArchData[] | null>(null);

    const navigate = useNavigate();
    const [isMenuOpen] = useMenuStore((state) => [state.isOpen, state.setIsOpen]);

    useEffect(() => {
        if (showPopup) {
            setLoading(true);
        }
    }, [showPopup]);

    useEffect(() => {
        if (setOnEdit) {
            const handleEdit = () => {
                setShowPopup(true);
                dispatchAsidePortalContentChanged(false);
            };
            setOnEdit(handleEdit);
        }
    }, []);

    useEffect(() => {
        if (toggleRequest) {
            (async () => await getArchDiagramData(true))();
        }
    }, [toggleRequest]);

    useEffect(() => {
        if (checkClicked) {
            (async () => await getArchDiagramData(false))();
        }
    }, [
        checkClicked,
        selectedData.selfManaged,
        selectedData.noFixedCapital,
        selectedData.openEnded,
        selectedData.corporate,
        selectedData.ifEltif,
    ]);

    const onDataChange = useCallback((investment: ArchNodeItem | null) => {
        setActiveInvestment(investment);
        if (investment) {
            setShowDetails(true);
        } else {
            setShowDetails(false);
            setActiveInvestment(null);
        }
    }, []);

    const closeDetails = () => {
        setShowDetails(false);
        setActiveInvestment(null);
    };

    const handleFormSubmission = async (formSelectedData: SelectedDataParams) => {
        const tmp = { ...selectedData, ...formSelectedData };

        setSelectedData({ ...tmp, selfManaged: false, noFixedCapital: true });
        setShowPopup(false);
        setToggleRequest(true);
    };

    const savePreset = async (event: any) => {
        event.preventDefault();

        try {
            await axios.post('/presets/legal-structures', selectedData);

            // redirect
            await navigate('/dashboard?save=1');
        } catch (err) {
            console.log(err);
            // setError(true);
        }
    };

    const getOpenEnded = async (assetClass: string) => {
        const { data } = await axios.get('/base/check/open-ended', {
            params: {
                assetClass: assetClass,
            },
        });

        return data;
    };

    const getArchDiagramData = async (toggleCheckboxes: boolean) => {
        if (!archDiagramData) {
            setLoading(true);
        }

        setCheckClicked(false);
        setToggleRequest(false);

        let isCorporate = selectedData.corporate;
        let isOpenEnded = selectedData.openEnded;
        let ifEltif = selectedData.ifEltif;

        const asset = selectedData.assets[0];

        let tmp = {
            ...selectedData,
        };

        if (toggleCheckboxes) {
            ifEltif = selectedData.investors
                .map((inv) => inv.id)
                .flat()
                .some((id) => [8, 17, 25, 30].includes(id!));

            const openEnded = await getOpenEnded(asset.name!);

            isCorporate = openEnded.legalType.trim() === 'corporate';
            isOpenEnded = openEnded.ifRecommendedOpenEnded;

            tmp = {
                ...tmp,
                corporate: isCorporate,
                openEnded: isOpenEnded,
                ifEltif: ifEltif,
            };
        }

        setSelectedData(tmp);
        dispatchAsidePortalContentChanged(true);

        try {
            const { data } = await axios.get('/legal-structures/diagram', {
                params: tmp,
            });

            setArchDiagramData(data);

            // Dispatch event once data is loaded from API
            // const shouldRenderPortal = document.querySelector('#aside');
        } catch (e) {
            setArchDiagramData(null);

            // Dispatch event when data fails to load
            // dispatchAsidePortalContentChanged(false);
        } finally {
            closeDetails();

            try {
                const tableUrl = new URLSearchParams(
                    // @ts-ignore
                    Object.entries(selectedData).map((entry) => {
                        // @ts-ignore
                        entry[1] = JSON.stringify(entry[1]);
                        return entry;
                    }),
                );

                setTableUrl('?' + tableUrl.toString());
                setSearchParams(tableUrl.toString());
            } catch (e) {
                console.log(e);
            }

            setLoading(false);
        }
    };

    const validateSelection = async (data: SelectedDataMulti) => {
        if (
            !data.assets.length ||
            !data.investors.length ||
            data.investors.filter((inv) => !inv.id).length ||
            data.assets.filter((inv) => !inv.id).length
        )
            return;

        for (const asset of data.assets) {
            if (asset.name) {
                const assetClass = asset.name;
                const result = await getOpenEnded(assetClass);

                await axios.get(`/legal-structures/validate-selection`, {
                    params: {
                        selfManaged: false,
                        noFixedCapital: true,
                        openEnded: result.ifRecommendedOpenEnded,
                        corporate: result.legalType === 'corporate',
                        ifEltif: false,
                        assets: data.assets,
                        investors: data.investors,
                    },
                });
            }
        }
    };

    return (
        <>
            <TransitionComponent inProp={showPopup} isAbsolute={true} duration={100}>
                <SelectionPopup isMulti={true} onSubmit={handleFormSubmission} validateSelection={validateSelection} />
            </TransitionComponent>

            <LoaderComponent inProp={pageReady} hideLoader={true}>
                <>
                    <ResultTable>
                        <ResultTableLeft />

                        <ResultTableMiddle>
                            <LoaderComponent inProp={!loading}>
                                <div className={styles.graph}>
                                    {archDiagramData && (
                                        <ArchDiagram
                                            dataFull={archDiagramData}
                                            onDataChange={onDataChange}
                                            activeCategory={activeInvestment}
                                        />
                                    )}
                                </div>
                            </LoaderComponent>
                        </ResultTableMiddle>

                        <ResultTableRight
                            showDetails={showDetails}
                            details={
                                <MainStructuresDetail activeInvestment={activeInvestment} onClose={closeDetails} />
                            }
                        />
                    </ResultTable>

                    <ResultTable isMain={false}>
                        <ResultTableLeft
                            legend={!isMenuOpen}
                            legends={[
                                `1: Approximate costs to cover all required service providers and setup. Please check the detailed overview for the breakdown.`,
                                `2: Minimum numbers of days to launch the structure (excluding any legal draftings, negotiations etc). Please check the detailed overview for the breakdown.`,
                            ]}
                            showComments={!isMenuOpen}
                        ></ResultTableLeft>
                        <ResultTableMiddle>
                            <div className={styles.actions}>
                                <LinkedButton
                                    to={{
                                        pathname: '/legal-structures/expanded',
                                        search: tableUrl,
                                    }}
                                    style={{
                                        minWidth: 170,
                                    }}
                                >
                                    Learn more
                                </LinkedButton>

                                <Button
                                    onClick={savePreset}
                                    style={{
                                        minWidth: 170,
                                    }}
                                >
                                    Save review
                                </Button>
                            </div>
                        </ResultTableMiddle>
                        <ResultTableRight showDetails={showDetails} tips={<Tips />}></ResultTableRight>
                    </ResultTable>
                </>
            </LoaderComponent>
        </>
    );
};

export default LegalStructures;
